import React, { useState, useEffect, useRef } from 'react';
import { X, Brain, Search, Upload, Sparkles, FileText, Check, AlertCircle, Loader2 } from 'lucide-react';

const FORMATS = [
  { id: 'multiple_choice', label: 'Multiple Choice', hint: '10 questions' },
  { id: 'true_false', label: 'True / False', hint: '15 questions' },
  { id: 'fill_blank', label: 'Fill in the Blank', hint: '8 questions' },
  { id: 'mixed', label: 'Mixed Format', hint: '12 questions' },
];

const TABS = [
  { id: 'library', label: 'From Library', icon: FileText },
  { id: 'upload', label: 'Upload a PDF', icon: Upload },
  { id: 'topic', label: 'From a Topic', icon: Sparkles },
];

// Real quiz creation, replacing what used to be a fully mocked "Go to Forms
// Builder" button that never actually generated anything. Reuses the exact
// generation pipeline already proven inside the PDF viewer's AI tools
// (POST /api/ai/quiz) rather than building a second one — the "Upload a PDF"
// tab reuses the normal upload endpoint too (as a Private note, so a
// throwaway quiz source doesn't clutter the public Library), then generates
// from the resulting material's id exactly the same way as picking one from
// the Library tab.
const CreateQuizModal = ({ resourceId, resourceTitle, resourcePages, token, onClose, onGenerated }) => {
  // Opened from a specific material's own "Create Quiz" icon (Library card,
  // My Notes item) — this always means "quiz me on THIS", so the picker/
  // upload/topic tabs are hidden entirely rather than asking the user to
  // pick a source they already picked by clicking that exact card.
  const locked = Boolean(resourceId);
  const [tab, setTab] = useState('library');
  const [format, setFormat] = useState('multiple_choice');
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  // "From Library" tab state
  const [selected, setSelected] = useState(resourceId ? { id: resourceId, title: resourceTitle, pages: resourcePages } : null);
  // Quiz scope: the whole document, or just a span of pages the student picks.
  const [pageMode, setPageMode] = useState('all');
  const [pageFrom, setPageFrom] = useState(1);
  const [pageTo, setPageTo] = useState(resourcePages || 1);
  const [scope, setScope] = useState('everyone'); // 'everyone' | 'mine'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchDebounce = useRef(null);

  // "Upload a PDF" tab state
  const [file, setFile] = useState(null);
  const [uploadProgressLabel, setUploadProgressLabel] = useState(null);

  // "From a Topic" tab state
  const [topic, setTopic] = useState('');

  const apiBase = process.env.REACT_APP_API_BASE_URL || '';

  const runSearch = async (text, searchScope) => {
    setSearching(true);
    try {
      const params = new URLSearchParams({ limit: '8' });
      if (text.trim()) params.set('search', text.trim());
      if (searchScope === 'mine') params.set('ownerOnly', 'true');
      const res = await fetch(`${apiBase}/api/materials/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setResults(res.ok ? (data.materials || []) : []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (tab !== 'library') return;
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => runSearch(query, scope), 300);
    return () => clearTimeout(searchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, scope, tab]);

  // Reset the page span whenever the chosen material changes, so a range
  // picked for one document never silently carries over to another.
  useEffect(() => {
    setPageMode('all');
    setPageFrom(1);
    setPageTo(selected?.pages || 1);
  }, [selected?.id, selected?.pages]);

  const generateFromPdfId = async (pdfId, usePageRange = true) => {
    const body = { pdfId, format };
    if (usePageRange && pageMode === 'range') {
      body.firstPage = Number(pageFrom) || 1;
      body.lastPage = Number(pageTo) || Number(pageFrom) || 1;
    }
    const res = await fetch(`${apiBase}/api/ai/quiz`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Quiz generation failed.');
    return data;
  };

  const handleGenerate = async () => {
    setError(null);
    setGenerating(true);
    try {
      if (tab === 'library') {
        if (!selected) throw new Error('Pick a material first.');
        const data = await generateFromPdfId(selected.id);
        onGenerated(data);
      } else if (tab === 'topic') {
        if (!topic.trim()) throw new Error('Enter a topic first.');
        const res = await fetch(`${apiBase}/api/ai/quiz`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ topic: topic.trim(), format }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Quiz generation failed.');
        onGenerated(data);
      } else if (tab === 'upload') {
        if (!file) throw new Error('Choose a PDF file first.');
        setUploadProgressLabel('Uploading...');
        const formData = new FormData();
        formData.append('files', file);
        formData.append('title', file.name.replace(/\.pdf$/i, ''));
        // Private: this note exists so a quiz can be generated from it, not
        // to publish it — it still shows up in the user's own My Notes.
        formData.append('visibility', 'Private');
        formData.append('category', 'Note');
        const uploadRes = await fetch(`${apiBase}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok) throw new Error(uploadData.error || 'Upload failed.');
        setUploadProgressLabel('Generating quiz...');
        // A just-uploaded file has no page picker yet, so it always quizzes
        // on the whole document.
        const data = await generateFromPdfId(uploadData.pdf.id, false);
        onGenerated(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
      setUploadProgressLabel(null);
    }
  };

  const canGenerate =
    (tab === 'library' && !!selected) ||
    (tab === 'topic' && topic.trim().length > 0) ||
    (tab === 'upload' && !!file);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
        <div className="bg-primary p-6 text-primary-foreground relative flex-shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 text-primary-foreground/60 hover:text-primary-foreground bg-transparent border-none cursor-pointer">
            <X className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 bg-card/20 rounded-2xl flex items-center justify-center mb-3">
            <Brain className="w-6 h-6" />
          </div>
          <h3 className="text-xl font-display font-bold">Create a Quiz</h3>
          <p className="text-primary-foreground/80 text-sm mt-1">
            {locked ? <>Generate a quiz from <strong>"{resourceTitle}"</strong>.</> : 'Generate real, interactive quiz questions with AI.'}
          </p>
        </div>

        {!locked && (
          <div className="flex border-b border-border flex-shrink-0">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold border-none cursor-pointer transition-colors ${
                  tab === t.id ? 'text-primary bg-primary/5 border-b-2 border-primary' : 'text-muted-foreground bg-transparent hover:text-foreground'
                }`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="p-6 space-y-5 overflow-y-auto">
          {locked && (
            <div className="flex items-center gap-2.5 px-3 py-3 rounded-xl border border-border bg-muted/30">
              <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-foreground font-medium truncate">{resourceTitle}</span>
            </div>
          )}

          {!locked && tab === 'library' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-background">
                <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for a note or book..."
                  className="flex-1 bg-transparent border-none outline-none text-sm text-foreground min-w-0"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setScope('everyone')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold border-none cursor-pointer transition-colors ${scope === 'everyone' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  Everyone
                </button>
                <button
                  onClick={() => setScope('mine')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold border-none cursor-pointer transition-colors ${scope === 'mine' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  My Notes
                </button>
              </div>

              <div className="max-h-52 overflow-y-auto space-y-1.5 -mx-1 px-1">
                {searching && <p className="text-xs text-muted-foreground text-center py-4">Searching...</p>}
                {!searching && results.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">No materials found.</p>
                )}
                {!searching && results.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setSelected({ id: m.id, title: m.title, pages: m.pages })}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border flex items-center gap-2.5 cursor-pointer transition-colors ${
                      selected?.id === m.id ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted/50'
                    }`}
                  >
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm text-foreground truncate flex-1">{m.title}</span>
                    {selected?.id === m.id && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                  </button>
                ))}
              </div>

              {selected && (
                <p className="text-[11px] text-muted-foreground">
                  Selected: <strong className="text-foreground">{selected.title}</strong>
                </p>
              )}
            </div>
          )}

          {!locked && tab === 'upload' && (
            <div className="space-y-3">
              <label className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl border-2 border-dashed border-border cursor-pointer hover:border-primary/50 transition-colors">
                <Upload className="w-6 h-6 text-muted-foreground" />
                <span className="text-sm text-foreground font-medium">{file ? file.name : 'Choose a PDF to quiz yourself on'}</span>
                <span className="text-[11px] text-muted-foreground">It's saved privately to your account, not published.</span>
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          )}

          {!locked && tab === 'topic' && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground block">Topic</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Photosynthesis, The French Revolution..."
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-background outline-none text-sm text-foreground"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground">The AI will build a quiz from its own knowledge of this topic — no document needed.</p>
            </div>
          )}

          {selected && (
            <div>
              <label className="text-xs font-bold text-muted-foreground mb-2 block">Pages</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPageMode('all')}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold border-none cursor-pointer transition-colors ${pageMode === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  Whole document
                </button>
                <button
                  onClick={() => setPageMode('range')}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold border-none cursor-pointer transition-colors ${pageMode === 'range' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  Page range
                </button>
              </div>
              {pageMode === 'range' && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="number"
                    min="1"
                    max={selected.pages || undefined}
                    value={pageFrom}
                    onChange={(e) => setPageFrom(e.target.value)}
                    className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none"
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <input
                    type="number"
                    min={pageFrom || 1}
                    max={selected.pages || undefined}
                    value={pageTo}
                    onChange={(e) => setPageTo(e.target.value)}
                    className="w-20 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none"
                  />
                  {selected.pages ? (
                    <span className="text-[11px] text-muted-foreground">of {selected.pages} pages</span>
                  ) : null}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-muted-foreground mb-2 block">Format</label>
            <div className="grid grid-cols-2 gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`text-left px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                    format === f.id ? 'border-primary bg-primary/5' : 'border-border bg-background hover:bg-muted/50'
                  }`}
                >
                  <p className="text-xs font-bold text-foreground">{f.label}</p>
                  <p className="text-[10px] text-muted-foreground">{f.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-destructive/10 text-destructive text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="p-6 pt-0 flex-shrink-0">
          <button
            onClick={handleGenerate}
            disabled={generating || !canGenerate}
            className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 border-none cursor-pointer shadow-lg shadow-primary/20"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> {uploadProgressLabel || 'Generating quiz...'}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Generate Quiz
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateQuizModal;
